import { ChevronLeft, ChevronRight, Search } from "lucide-react";
import type { KeyboardEvent } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import type { PlatformMeta } from "@/lib/platform-api";

export type PlatformFilterOption = { value: string; label: string };

type PlatformListToolbarProps = {
  searchPlaceholder: string;
  search: string;
  onSearchChange: (value: string) => void;
  onSearchSubmit?: () => void;
  filterValue: string;
  onFilterChange: (value: string) => void;
  filterOptions: PlatformFilterOption[];
  meta: PlatformMeta | null;
  page: number;
  onPageChange: (page: number) => void;
  loading?: boolean;
};

export function PlatformListToolbar({
  searchPlaceholder,
  search,
  onSearchChange,
  onSearchSubmit,
  filterValue,
  onFilterChange,
  filterOptions,
  meta,
  page,
  onPageChange,
  loading = false,
}: PlatformListToolbarProps) {
  const total = meta?.total ?? 0;
  const perPage = meta?.per_page ?? 20;
  const totalPages = total > 0 ? Math.ceil(total / perPage) : 1;
  const rangeStart = total === 0 ? 0 : (page - 1) * perPage + 1;
  const rangeEnd = total === 0 ? 0 : Math.min(page * perPage, total);

  function handleSearchKeyDown(event: KeyboardEvent<HTMLInputElement>) {
    if (event.key === "Enter") {
      onSearchSubmit?.();
    }
  }

  return (
    <div className="flex flex-col gap-3 sm:flex-row sm:flex-wrap sm:items-center sm:justify-between">
      <div className="flex flex-1 flex-wrap items-center gap-2">
        <div className="relative min-w-[200px] flex-1 sm:max-w-sm">
          <Search className="pointer-events-none absolute top-1/2 left-3 size-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            value={search}
            onChange={(event) => onSearchChange(event.target.value)}
            onKeyDown={handleSearchKeyDown}
            placeholder={searchPlaceholder}
            className="pl-9"
            aria-label="搜索"
          />
        </div>
        <Select value={filterValue || "all"} onValueChange={(value) => onFilterChange(value === "all" ? "" : value)}>
          <SelectTrigger size="sm" className="w-[140px]" aria-label="筛选">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {filterOptions.map((option) => (
              <SelectItem key={option.value || "all"} value={option.value || "all"}>
                {option.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      <div className="flex items-center gap-2 text-sm text-muted-foreground">
        <span className="tabular-nums">
          {loading ? "加载中…" : total === 0 ? "暂无数据" : `第 ${rangeStart}–${rangeEnd} 条，共 ${total} 条`}
        </span>
        <Button
          type="button"
          variant="outline"
          size="sm"
          disabled={loading || page <= 1}
          onClick={() => onPageChange(page - 1)}
          aria-label="上一页"
        >
          <ChevronLeft className="size-4" />
          上一页
        </Button>
        <span className="tabular-nums">
          {page} / {totalPages}
        </span>
        <Button
          type="button"
          variant="outline"
          size="sm"
          disabled={loading || page >= totalPages}
          onClick={() => onPageChange(page + 1)}
          aria-label="下一页"
        >
          下一页
          <ChevronRight className="size-4" />
        </Button>
      </div>
    </div>
  );
}
